/**
 * Kanban schemas — mirror chaos.boards / chaos.columns / chaos.cards
 * per KANBAN_MODULE_v2.md and migrations 022_kanban_core + 023_kanban_realtime.
 *
 * These Zod schemas are the *single source of truth* shared between
 * chaos-gateway (Drizzle queries return rows validated against them)
 * and chaos-ui (TanStack Query caches typed responses + form
 * validation in inline editors).
 */
import { z } from 'zod';
import { cardLiveStateSchema } from './live-state';

// Card liveness is part of the kanban wire contract, but its resolver lives in
// a side-effect-free module so gateway and UI consumers share one ladder.
export * from './live-state';

// ── Autonomy ─────────────────────────────────────────────────────────
// Matches CHECK constraint on chaos.columns.default_autonomy_level and
// chaos.cards.autonomy_level. ``agent_only`` is the most autonomous tier
// (no human review); ``HITL`` is the most cautious (Human-in-the-Loop).
export const autonomyLevels = ['HITL', 'HOTL', 'HOOL'] as const; // 3 modes only (founder 2026-06-27); legacy agent_only merged into HOOL (migration 0092), gateway folds strays
export const autonomyLevelSchema = z.enum(autonomyLevels);
export type AutonomyLevel = z.infer<typeof autonomyLevelSchema>;

// ── Priority ─────────────────────────────────────────────────────────
// Matches CHECK constraint on chaos.cards.priority (migration 0075).
// 'high' = urgent / do-first, 'mid' = the neutral default, 'low' =
// can-wait. Stored as the raw machine token; the UI renders a small
// coloured glyph (kanban-card) + a 3-way picker (card-detail header).
export const cardPriorities = ['high', 'mid', 'low'] as const;
export const cardPrioritySchema = z.enum(cardPriorities);
export type CardPriority = z.infer<typeof cardPrioritySchema>;

// ── Board ────────────────────────────────────────────────────────────
// Post-Phase-5 (Alembic 024): projectId is NOT NULL — every board lives
// inside a project. Older API consumers can still send a nullable
// payload — the gateway resolves a default project per workspace when
// projectId is omitted.
export const boardSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string().min(1).max(160),
  description: z.string().nullable(),
  createdBy: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  archivedAt: z.string().datetime().nullable(),
});
export type Board = z.infer<typeof boardSchema>;

// Board templates control how the gateway seeds default columns when a
// board is created. ``kanban-default`` populates Backlog / In Progress /
// Review / Done columns automatically (Phase 5 default).
export const boardTemplates = ['empty', 'kanban-default'] as const;
export const boardTemplateSchema = z.enum(boardTemplates);
export type BoardTemplate = z.infer<typeof boardTemplateSchema>;

export const createBoardSchema = z.object({
  name: z.string().min(1).max(160),
  description: z.string().max(2000).nullable().optional(),
  projectId: z.string().uuid().optional(),
  template: boardTemplateSchema.optional(),
});
export type CreateBoardInput = z.infer<typeof createBoardSchema>;

export const updateBoardSchema = z.object({
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(2000).nullable().optional(),
  projectId: z.string().uuid().optional(),
});
export type UpdateBoardInput = z.infer<typeof updateBoardSchema>;

// ── Column ───────────────────────────────────────────────────────────
export const columnSchema = z.object({
  id: z.string().uuid(),
  boardId: z.string().uuid(),
  name: z.string().min(1).max(80),
  position: z.number().int().nonnegative(),
  color: z.string().nullable(),
  watcherAgentId: z.string().uuid().nullable(),
  defaultAutonomyLevel: autonomyLevelSchema,
  // Optional URL-safe identifier minted at seed time (e.g. 'backlog',
  // 'archive'). Surfaced 2026-05-25 so the UI can recognise the
  // archive column and apply collapse-by-default semantics without
  // matching on display name (which is user-editable).
  slug: z.string().nullable().optional(),
  // 0076 — canonical pipeline role (backlog|todo|formulating|in_progress|
  // for_approval|done|archive|custom). NULL on pre-migration columns.
  // Lets the UI resolve a column's role without name-matching.
  kind: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
});
export type Column = z.infer<typeof columnSchema>;

export const createColumnSchema = z.object({
  name: z.string().min(1).max(80),
  position: z.number().int().nonnegative().optional(),
  color: z.string().max(32).nullable().optional(),
  watcherAgentId: z.string().uuid().nullable().optional(),
  defaultAutonomyLevel: autonomyLevelSchema.optional(),
});
export type CreateColumnInput = z.infer<typeof createColumnSchema>;

export const updateColumnSchema = z.object({
  name: z.string().min(1).max(80).optional(),
  position: z.number().int().nonnegative().optional(),
  color: z.string().max(32).nullable().optional(),
  watcherAgentId: z.string().uuid().nullable().optional(),
  defaultAutonomyLevel: autonomyLevelSchema.optional(),
});
export type UpdateColumnInput = z.infer<typeof updateColumnSchema>;

// ── Card ─────────────────────────────────────────────────────────────
// ``position`` is FLOAT for fractional indexing (drop-between-two-cards
// without renumbering the whole column). Rebalance is triggered backend-
// side when float precision degrades.
export const cardSchema = z.object({
  id: z.string().uuid(),
  workspaceId: z.string().uuid(),
  boardId: z.string().uuid(),
  columnId: z.string().uuid(),
  title: z.string().min(1).max(280),
  description: z.string().nullable(),
  position: z.number(),
  primaryAgentId: z.string().uuid().nullable(),
  presetId: z.string().uuid().nullable(),
  autonomyLevel: autonomyLevelSchema.nullable(),
  // Per-card priority (migration 0075). NOT NULL in the DB (DEFAULT
  // 'mid'); `.default('mid')` here keeps older API responses that omit
  // the field parsing cleanly.
  priority: cardPrioritySchema.default('mid'),
  // Phase 12 / Gantt: target completion + caller-supplied estimate.
  // Both nullable so legacy rows render fine without a backfill.
  // ``dueDate`` is ISO 8601; ``estimatedHours`` is bounded by the DB
  // CHECK constraint (>=0).
  dueDate: z.string().datetime().nullable().default(null),
  estimatedHours: z.number().int().nonnegative().nullable().default(null),
  createdBy: z.string().uuid(),
  // Sealed creator stamp (migration 0077). Set ONCE on create, never editable.
  //   createdByUserId — the auth.users id of the human (or, in future, the
  //     Conductor's owner) who created the card. NULL for legacy cards that
  //     predate the stamp → the UI renders "Created by —".
  //   createdVia — 'user' for a human-created card, 'conductor' for a future
  //     Conductor-on-behalf card (UI renders "Created by <name>'s Conductor").
  //   createdByName — server-resolved auth.users.display_name for
  //     createdByUserId, surfaced by GET so the context menu needs no extra
  //     round-trip. NULL when the creator is unknown / display_name is unset.
  // All three optional so older API responses (e.g. the board list) still parse.
  createdByUserId: z.string().uuid().nullable().optional(),
  createdVia: z.enum(['user', 'conductor']).optional(),
  createdByName: z.string().nullable().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  archivedAt: z.string().datetime().nullable(),
  // Phase B (Sessions runtime): true iff the card has at least one
  // session in state IN (active, waking, sleeping). Drives the green
  // dot in KanbanCard. Optional so older API responses still parse.
  hasActiveSession: z.boolean().optional(),
  // Phase B+ (2026-05-20): per-card highest-priority session state
  // for the indicator dot color (green=active, yellow=sleeping/waking).
  // DEPRECATED 2026-05-25 — kept on the wire for API back-compat but
  // the UI now consults the richer signals below instead.
  sessionState: z.enum(["active", "waking", "sleeping"]).nullable().optional(),
  // Card-as-Session indicator signals (2026-05-25, founder spec).
  // The old "container state == indicator color" mapping was a relic
  // of Docker-per-card; with ephemeral runs + sleep/wake we colour the
  // dot by the most recent RUN, not by whether a session record exists.
  //   currentRunActive — at least one chaos.session_runs row for this
  //     card has status IN ('starting','running') (ended_at IS NULL).
  //     Maps to GREEN pulsing dot.
  //   lastRunEndedAt — MAX(session_runs.ended_at) for this card,
  //     capped at NULL if older than 24h. Drives YELLOW recency dot.
  //   lastRunFailed — most recent ENDED run within the 24h window
  //     finished with status IN ('failed','timeout') OR exit_code != 0.
  //     Maps to RED error dot (only inside the same 24h window).
  //   lastRunErrorMessage — first ~120 chars of session_runs.error_message
  //     for the failing run, used as the indicator tooltip body.
  //   hitlPending — at least one chaos.call_questions for this card
  //     has status='asked'. Maps to PURPLE awaiting-input dot.
  // All five fields are optional so older API responses still parse,
  // and the resolver in lib/kanban/card-status.ts treats absent fields
  // as "no signal" (falls back to no indicator).
  currentRunActive: z.boolean().optional(),
  // C2 authoritative card/session/run tuple. Nullable only during a rolling
  // gateway deploy; once present it supersedes the legacy booleans below.
  liveState: cardLiveStateSchema.nullable().optional(),
  lastRunEndedAt: z.string().datetime().nullable().optional(),
  // W1 (design master spec §3.4) — UNCAPPED MAX(session_runs.ended_at) for this
  // card (NO 24h cap, unlike lastRunEndedAt above). Drives the kanban card meta
  // "activity" chip's >24h DATE case ("Jul 5"). Optional so older payloads parse;
  // the UI falls back to lastRunEndedAt (chip simply absent >24h) until the
  // gateway ships it.
  lastAgentActivityAt: z.string().datetime().nullable().optional(),
  lastRunFailed: z.boolean().optional(),
  lastRunErrorMessage: z.string().nullable().optional(),
  hitlPending: z.boolean().optional(),
  // §Y (2026-07-27) — "the ball is in your court". The agent FINISHED what was
  // asked, the card is not done, and NOTHING IS BLOCKED. Distinct from
  // hitlPending, which means the agent stopped and CANNOT continue until a
  // human acts; this one means it simply has nothing further to do until he
  // looks. Blue raised hand vs amber hourglass on the board.
  //
  // Server-derived from chaos.session_runs (gateway mentions.ts
  // cardBallInHumanCourtClause) — zero writers, no row of its own, so unlike
  // hitlPending it cannot go stale. Structurally disjoint from hitlPending: the
  // clause's first term is NOT cardWaitingOnHumanClause(), so a card can never
  // report both. Optional so older payloads parse; absent ⇒ the resolver never
  // reaches the 'yourTurn' branch and the board is byte-identical to before.
  yourTurn: z.boolean().optional(),
  // Status-glyph redesign (2026-07-08). Two extra signals so the unified card
  // status glyph can distinguish a live run that has gone quiet from one that
  // is still working, and so a card that has EVER run keeps a resting "done"
  // glyph instead of vanishing after the 24h recency window.
  //   heartbeatStale — currentRunActive AND the freshest active run's
  //     last_heartbeat_at is older than ~60s (the same threshold the
  //     server-side stale-run sweep uses). Maps to the grey Pause glyph.
  //   everStarted    — the card has launched at least one session_runs row
  //     (or has any session), regardless of age. Keeps the resting green
  //     Check glyph on the board forever once a session has started.
  // Both optional so older API responses parse; the glyph resolver treats an
  // absent heartbeatStale as "fresh" and an absent everStarted as "never ran".
  heartbeatStale: z.boolean().optional(),
  everStarted: z.boolean().optional(),
  // STUCK_GLYPH_V2 (2026-07-10, gateway flag STUCK_GLYPH_V2, default OFF). Split
  // the two "not-working" signals the glyph currently conflates:
  //   outputStale — the card has a LIVE run whose heartbeat is FRESH (pulse
  //     < 60s) but which has produced NO new agent output for longer than
  //     STUCK_OUTPUT_GAP_MS (default 2 min) — i.e. alive but silent/frozen.
  //     Drives the grey Pause "stuck" glyph. With the flag ON a STALE heartbeat
  //     instead routes to the red error glyph (died / lost contact).
  //   lastOutputAt — ISO of the newest agent output (session_messages) for the
  //     active run, or null. Feeds the "no output for Nm" tooltip.
  // BOTH optional and ONLY emitted when the gateway flag is ON, so a flag-OFF
  // board parses with them absent and the resolver falls back to the current
  // heartbeat-based 'stalled' (byte-identical to today).
  outputStale: z.boolean().optional(),
  lastOutputAt: z.string().datetime().nullable().optional(),
  // Conductor Phase 4 (SCHEDULER_ENABLED) — the card has a live launch parked
  // in chaos.task_queue (state IN queued/admitting) waiting for capacity. The
  // board card-list folds these in (no N+1) so the ⏳ + ETA render without a
  // per-card probe. All optional: the gateway only emits them when
  // SCHEDULER_ENABLED is ON, so a flag-OFF board parses with the fields absent
  // and never shows a ⏳ (card-status.ts resolver treats absent as "no signal").
  //   queued       — there is a live queue row for this card.
  //   queuedReason — why it's waiting (task_queue.blocked_reason).
  //   queuedEtaAt  — ISO next-window reset for the ETA clause, or null.
  queued: z.boolean().optional(),
  queuedReason: z
    .enum(["max_concurrent", "ram", "cpu", "window_5h", "window_weekly", "window_exhausted"])
    .nullable()
    .optional(),
  queuedEtaAt: z.string().datetime().nullable().optional(),
  // M2.1 extensions (2026-05-20) — surfaced through GET /api/kanban/cards/:cardId
  // so the UI can read the new fields without a second round-trip.
  assigneeKind: z.enum(["user", "role", "agent_instance"]).nullable().optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  assigneeRoleId: z.string().uuid().nullable().optional(),
  // Migration 0414 — the card's ONE responsible person ("a person is
  // responsible, the agent helps"). responsibleUserId is on the board list and
  // the single-card GET; responsibleName (display name, falling back to nothing)
  // only on the single-card GET, like createdByName. Both optional so older
  // API responses still parse.
  responsibleUserId: z.string().uuid().nullable().optional(),
  responsibleName: z.string().nullable().optional(),
  roleId: z.string().uuid().nullable().optional(),
  // Ordered role-member ids (chaos.card_members, member_kind='role') surfaced by
  // the board card-list so the meta line can show "first role + N more". Empty /
  // absent when a card has no explicit role members (UI falls back to roleId).
  memberRoleIds: z.array(z.string().uuid()).optional(),
  cardInstructions: z.string().nullable().optional(),
  workflow: z.unknown().nullable().optional(),
  approvalPolicy: z.string().nullable().optional(),
  // X6 (2026-05-21) — canonical /srv/chaos/orgs/.../tasks/<id>/workspace
  // path surfaced by getCard so "Open in Explorer" can deep-link
  // without re-fetching. Nullable: legacy cards pre-M-X1 backfill
  // have no host folder yet.
  hostWorkspacePath: z.string().nullable().optional(),
});
export type Card = z.infer<typeof cardSchema>;

export const createCardSchema = z.object({
  boardId: z.string().uuid(),
  columnId: z.string().uuid(),
  title: z.string().min(1).max(280),
  description: z.string().max(50_000).nullable().optional(),
  position: z.number().optional(),
  primaryAgentId: z.string().uuid().nullable().optional(),
  presetId: z.string().uuid().nullable().optional(),
  autonomyLevel: autonomyLevelSchema.nullable().optional(),
  priority: cardPrioritySchema.optional(),
  dueDate: z.string().datetime().nullable().optional(),
  estimatedHours: z.number().int().nonnegative().nullable().optional(),
});
export type CreateCardInput = z.infer<typeof createCardSchema>;

export const updateCardSchema = z.object({
  title: z.string().min(1).max(280).optional(),
  description: z.string().max(50_000).nullable().optional(),
  columnId: z.string().uuid().optional(),
  position: z.number().optional(),
  primaryAgentId: z.string().uuid().nullable().optional(),
  presetId: z.string().uuid().nullable().optional(),
  autonomyLevel: autonomyLevelSchema.nullable().optional(),
  priority: cardPrioritySchema.optional(),
  dueDate: z.string().datetime().nullable().optional(),
  estimatedHours: z.number().int().nonnegative().nullable().optional(),
});
export type UpdateCardInput = z.infer<typeof updateCardSchema>;

// Atomic move endpoint — guarantees row-level lock so two concurrent
// drag-drops on the same card can't race.
export const moveCardSchema = z.object({
  columnId: z.string().uuid(),
  position: z.number(),
});
export type MoveCardInput = z.infer<typeof moveCardSchema>;

// ── Real-time WebSocket event ────────────────────────────────────────
// Emitted by chaos.notify_board_event() trigger, forwarded by gateway.
export const boardEventOpSchema = z.enum(['INSERT', 'UPDATE', 'DELETE']);
export const boardEventTableSchema = z.enum(['cards', 'columns']);

export const boardEventSchema = z.object({
  event: boardEventOpSchema,
  table: boardEventTableSchema,
  boardId: z.string().uuid(),
  data: z.record(z.unknown()),
});
export type BoardEvent = z.infer<typeof boardEventSchema>;
